import { z } from "zod";
import { mapValues } from "../../../lib/functional";
import { StableValue } from "../../../lib/stable-value";
import { safeParse } from "../../../lib/zod";
import {
  SerializationSchema
} from "../../../serialization";
import {
  ControlDefinition,
  serialize
} from "../../definition";
import { ShapeControl } from "./shape-control";
class Definition extends ControlDefinition {
  static type = "makeswift::controls::shape";
  static deserialize(data, deserializeCallback) {
    if (data.type !== Definition.type)
      throw new Error(
        `Shape: expected '${Definition.type}', got '${data.type}'`
      );
    const {
      config: { type, ...config }
    } = z.object({
      type: z.literal(Definition.type),
      config: z.object({
        type: z.record(z.string(), SerializationSchema.deserializedRecord)
      })
    }).parse(data);
    const deserializedType = mapValues(type, (itemDef) => {
      return deserializeCallback(itemDef);
    });
    return Shape({
      type: deserializedType,
      ...config
    });
  }
  get keyDefs() {
    return this.config.type;
  }
  get controlType() {
    return Definition.type;
  }
  get schema() {
    const type = z.literal(Definition.type);
    const keys = mapValues(this.keyDefs, (def) => def.schema);
    const data = z.object(mapValues(keys, (key) => key.data));
    const value = z.object(mapValues(keys, (key) => key.value));
    const resolvedValue = z.object(
      mapValues(keys, (key) => key.resolvedValue)
    );
    const config = z.object({
      type: z.object(mapValues(keys, (key) => key.definition))
    });
    const definition = z.object({
      type,
      config
    });
    return { type, data, value, resolvedValue, definition };
  }
  safeParse(data) {
    return safeParse(this.schema.data, data);
  }
  fromData(data) {
    if (data == null)
      return void 0;
    return mapValues(
      this.keyDefs,
      (def, key) => def.fromData(data[key])
    );
  }
  toData(value) {
    return mapValues(this.keyDefs, (def, key) => def.toData(value[key]));
  }
  copyData(data, context) {
    if (data == null)
      return void 0;
    return mapValues(
      this.keyDefs,
      (def, key) => def.copyData(data[key], context)
    );
  }
  getTranslatableData(data) {
    if (data == null)
      return null;
    return mapValues(this.keyDefs, (def, key) => {
      return def.getTranslatableData(data[key]);
    });
  }
  mergeTranslatedData(data, translatedData, context) {
    if (translatedData == null)
      return data;
    return mapValues(
      this.keyDefs,
      (def, key) => def.mergeTranslatedData(data[key], translatedData[key], context)
    );
  }
  resolveValue(data, resolver, stylesheet, control) {
    const keyValues = mapValues(
      this.keyDefs,
      (def, key) => def.resolveValue(
        data?.[key],
        resolver,
        stylesheet.child(key),
        control?.child(key)
      )
    );
    const stableValue = StableValue({
      name: Definition.type,
      read: () => mapValues(keyValues, (v) => v.readStable()),
      deps: Object.values(keyValues)
    });
    return {
      ...stableValue,
      triggerResolve: async (currentValue) => {
        await Promise.all(
          Object.entries(keyValues).map(
            ([key, v]) => v?.triggerResolve(currentValue?.[key])
          )
        );
      }
    };
  }
  createInstance(sendMessage) {
    return new ShapeControl(this, sendMessage);
  }
  serialize() {
    return serialize(this.config, {
      type: Definition.type
    });
  }
  introspect(data, target) {
    if (data == null)
      return [];
    return Object.entries(this.keyDefs).flatMap(
      ([key, def]) => def.introspect(data[key], target) ?? []
    );
  }
}
class ShapeDefinition extends Definition {
}
function Shape(config) {
  return new ShapeDefinition(config);
}
export {
  Shape,
  ShapeDefinition
};
//# sourceMappingURL=shape.js.map